import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import DesignCard from '../../../../shared/components/DesignCard';
import { publicImageUrl } from '../../../lib/publicImageUrl';
import { formatMessage } from '@99designs/frontend-components/i18n';
import Attribution from '../../../../shared/components/Attribution';
import { Image } from '../CategoryConfigProvider';
import { spaces } from '../../../../shared/lib/styles';
import { IDLE_STATE_REACHED } from '../../../lib/constants';
import { between } from '../../../../shared/spa/core-components/utils/media';

const extraSmallDesign = '120px';
const smallDesign = '160px';
const mediumDesign = '200px';
const largeDesign = '300px';

const defaultDesignCardPositions = [
    [-1050, -260],
    [-860, -195],
    [-890, 270],
    [-700, 480],
    [-385, 500],
    [-50, 460],
    [135, 380],
    [540, 420],
    [340, -240],
    [755, -245],
    [400, 80],
    [605, -155],
];

const designCardWithHeroImgPositions = [...defaultDesignCardPositions];
designCardWithHeroImgPositions[6] = [320, 600];
designCardWithHeroImgPositions[7] = [740, 550];

const float = (position: number[], speed: number) => {
    const x = position[0];
    const y = position[1];

    // rotate required on the transform to render
    // animation smoothly on Firefox
    return keyframes`
        from {
            transform: translate(${x}px, ${y}px);
        }
        33% {
            transform: rotate(0.001deg) translate(${x + (x / 50) * speed}px, ${
        y + (y / 100) * speed
    }px) );
        }
        66% {
            transform: rotate(0.001deg) translate(${x + (x / 100) * speed}px, ${
        y + (y / 50) * speed
    }px) ;
        }
        to {
            transform: translate(${x}px, ${y}px);
        }
    `;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getDesignCardPosition = (index: any, isHeroImgExist: any) => {
    if (isHeroImgExist) {
        return designCardWithHeroImgPositions[index];
    }

    return defaultDesignCardPositions[index];
};

const DesignCardWrapper = styled.div<{
    index: number;
    isHeroView?: boolean;
    hasFeatureImage?: boolean;
}>(({ index, isHeroView, hasFeatureImage }) => {
    const position = getDesignCardPosition(index, hasFeatureImage);
    const movingSpeed = index % 2 ? 2 : -2;
    return css`
        display: inline-block;

        ${between('small', 'medium')} {
            width: ${largeDesign};
            height: ${largeDesign};
            margin-right: ${spaces.halfGutter}px;

            &:last-child {
                margin-right: 0;
            }
        }

        ${between('normal', 'large')} {
            position: absolute;
            z-index: 1;
            top: 0;
            left: 0;
            width: ${extraSmallDesign};
            height: ${extraSmallDesign};
            transform: translate(${position[0]}px, ${position[1]}px);
            transition: opacity 0.3s, visibility 0.3s;
            animation-name: ${float(position, movingSpeed)};
            animation-duration: 11s;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
            animation-play-state: paused;

            &:nth-child(1),
            &:nth-child(3),
            &:nth-child(4) {
                ${css`
                    width: ${smallDesign};
                    height: ${smallDesign};
                `}
            }

            &:nth-child(7) {
                ${css`
                    width: ${largeDesign};
                    height: ${largeDesign};
                `}
            }

            &:nth-child(11) {
                ${css`
                    width: ${mediumDesign};
                    height: ${mediumDesign};
                `}
            }

            ${!isHeroView &&
            css`
                visibility: hidden;
                opacity: 0;
            `}

            ${`.${IDLE_STATE_REACHED}`} & {
                animation-play-state: running;
            }
        }
    `;
});

interface Props {
    index: number;
    image: Image;
    categoryTitle: string;
    isHeroView?: boolean;
    hasFeatureImage?: boolean;
}

export default function HeroDesignCard({
    index,
    image,
    categoryTitle,
    isHeroView,
    hasFeatureImage,
}: Props) {
    return (
        <DesignCardWrapper index={index} isHeroView={isHeroView} hasFeatureImage={hasFeatureImage}>
            <DesignCard
                src={publicImageUrl(image.src)}
                alt={formatMessage(__("{categoryTitle} with the title '{imageTitle}'"), {
                    categoryTitle: categoryTitle,
                    imageTitle: image.title,
                })}
                width={300}
                height={300}
                variations={['medium-shadow']}
                attribution={<Attribution name={image.designer} />}
                lazyload={false}
            />
        </DesignCardWrapper>
    );
}
